import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';

import {
    fetchOpenTicketsAssignedToUserLatest,
    fetchOpenTicketsAssignedToUserNext,
} from 'flow/actions/tickets-action';

import EmptyBubble from 'flow/components/nav/empty-states/EmptyBubble';
import TicketsList from 'flow/components/tickets/tickets-list/TicketsList';
import {
    selectApiHasMoreOpenTicketsAssignedToLoggedInUserData,
    selectTicketKeysMineSorted,
    selectTicketsSortField,
    selectTicketsSortDirection,
} from 'flow/selectors/TicketsSelectors';
import { Seq } from 'immutable';

const SEQ_EMPTY: Seq.Indexed<TicketKey> = Seq.Indexed();

interface Props {
    itemSelectedKey?: TicketKey;
    scrollableTargetId: string;
    listItemClicked: (key: TicketKey) => void;
}

const TicketsListMineWrapper = ({
    itemSelectedKey,
    scrollableTargetId,
    listItemClicked,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [isFetchingLatestData, setIsFetchingLatestData] = useState(true);

    const apiHasMoreData = useAppSelector(
        selectApiHasMoreOpenTicketsAssignedToLoggedInUserData,
    );

    const itemKeysList =
        useAppSelector(selectTicketKeysMineSorted) || SEQ_EMPTY;

    const sortField = useAppSelector(selectTicketsSortField);
    const sortDirection = useAppSelector(selectTicketsSortDirection);

    useEffect(() => {
        setIsFetchingLatestData(true);
        dispatch(fetchOpenTicketsAssignedToUserLatest).finally(() => {
            setIsFetchingLatestData(false);
        });
    }, [dispatch, setIsFetchingLatestData, sortField, sortDirection]);

    const fetchNext = useCallback(
        () => dispatch(fetchOpenTicketsAssignedToUserNext),
        [dispatch],
    );

    return itemKeysList.isEmpty() && !isFetchingLatestData ? (
        <EmptyBubble
            title={i18next.t('tickets.no_tickets')}
            imageId={'supervisorCircle'}
        />
    ) : (
        <TicketsList
            key={'TICKETS_MINE_WRAPPER'}
            itemKeys={itemKeysList}
            handleListItemClicked={listItemClicked}
            itemSelectedKey={itemSelectedKey}
            displayLoadingItem={isFetchingLatestData && itemKeysList.isEmpty()}
            onReachedEndOfList={fetchNext}
            canFetchMoreData={apiHasMoreData}
            scrollableTargetId={scrollableTargetId}
        />
    );
};

export default React.memo(TicketsListMineWrapper);
